import { useEffect, useState, useRef } from "react";
import { CalendarCheck, Users, Building, Award } from "lucide-react";

const stats = [
  { icon: CalendarCheck, value: 325, suffix: "+", label: "Atendimentos", sublabel: "últimos 12 meses" },
  { icon: Users, value: 15, suffix: "", label: "Profissionais Ativos", sublabel: "atendendo regularmente" },
  { icon: Building, value: 20, suffix: "", label: "Profissionais", sublabel: "cadastrados na plataforma" },
  { icon: Award, value: 1, suffix: "", label: "Sala Premium", sublabel: "totalmente equipada" },
];

const useCountUp = (target: number, duration: number, start: boolean) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!start) return;

    let startTime: number | null = null;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [target, duration, start]);

  return count;
};

const StatCard = ({ stat: { icon: Icon, value, suffix, label, sublabel }, delay }: { stat: typeof stats[0]; delay: number }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const count = useCountUp(value, 1500, visible);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setVisible(true), delay);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [delay]);

  return (
    <div ref={ref} className="text-center p-6 md:p-8 rounded-2xl bg-white/90 border border-emerald-100/30 shadow-lg hover:shadow-xl transition-all duration-300 group">
      <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-4 md:mb-5 group-hover:scale-110 transition-transform duration-300">
        <Icon className="w-7 h-7 md:w-8 md:h-8 text-white" />
      </div>
      <div className="text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-1">
        {count}{suffix}
      </div>
      <div className="text-sm md:text-base font-semibold text-gray-800">{label}</div>
      <div className="text-xs md:text-sm text-gray-500 mt-1">{sublabel}</div>
    </div>
  );
};

const StatsSection = () => {
  return (
    <section id="stats" className="py-16 lg:py-24 px-4 bg-gradient-to-br from-emerald-50/40 via-teal-50/40 to-blue-50/40 relative overflow-hidden">
      <div className="absolute inset-0 bg-white/20" />

      <div className="container mx-auto max-w-6xl relative z-10">
        <div className="text-center mb-10 lg:mb-14">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-4 lg:mb-6">
            Nossos Números
          </h2>
          <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Resultados reais que refletem a confiança dos profissionais de Santa Maria
          </p>
          <div className="w-16 lg:w-24 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 mx-auto mt-6 rounded-full" />
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 lg:gap-8">
          {stats.map((stat, index) => (
            <StatCard key={index} stat={stat} delay={index * 150} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
